<template>
  <transition name="ck">
    <section
      v-if="visible"
      class="ck"
      role="dialog"
      aria-live="polite"
      aria-labelledby="ck-titulo"
      aria-describedby="ck-texto"
    >
      <div class="ck-inner">
        <span class="ck-icon material-symbols-outlined" aria-hidden="true">cookie</span>

        <div class="ck-body">
          <h2 id="ck-titulo" class="ck-title">Cookies de estadísticas</h2>
          <p id="ck-texto" class="ck-text">
            Usamos Google Analytics para saber, de forma anónima, cómo se usa la web y mejorarla.
            Las cookies solo se guardan si las aceptas. No usamos cookies de publicidad.
          </p>

          <div v-if="detalle" class="ck-detail">
            <p>
              <strong>Qué se recoge:</strong> páginas visitadas, tiempo en la web, hasta dónde se baja en cada página,
              en qué enlaces, botones e imágenes se hace clic, tipo de dispositivo y ciudad aproximada.
              Nunca se recoge lo que escribes en formularios. Google Analytics anonimiza la dirección IP.
            </p>
            <p>
              <strong>Cookies:</strong> <code>_ga</code> y <code>_ga_*</code> (Google Analytics, hasta 2 años).
              Si las rechazas, la web funciona igual y solo se envían estadísticas anónimas sin cookies
              ni identificador, que no permiten reconocerte entre visitas.
            </p>
            <p>
              Puedes cambiar tu decisión cuando quieras desde el enlace <em>«Configurar cookies»</em> al pie de la página.
            </p>
          </div>

          <button type="button" class="ck-link" :aria-expanded="detalle" @click="detalle = !detalle">
            {{ detalle ? "Ocultar detalles" : "Más información" }}
          </button>
        </div>

        <div class="ck-actions">
          <button type="button" class="ck-btn ck-btn--ghost" @click="decidir('denied')">Rechazar</button>
          <button type="button" class="ck-btn" @click="decidir('granted')">Aceptar</button>
        </div>
      </div>
    </section>
  </transition>
</template>

<script>
import { estadoConsentimiento, guardarConsentimiento, track } from "@/directives/analitica.js";

export default {
  name: "AvisoCookies",
  data() {
    return {
      detalle: false,
    };
  },
  computed: {
    // Se muestra si el visitante aún no ha decidido o si lo reabre desde el pie
    visible() {
      return estadoConsentimiento.valor === null || estadoConsentimiento.panelAbierto;
    },
  },
  methods: {
    decidir(valor) {
      guardarConsentimiento(valor);
      if (valor === "granted") track("consentimiento_cookies", { decision: "aceptadas" });
      this.detalle = false;
    },
  },
};
</script>

<style scoped>
.ck {
  position: fixed;
  right: 16px;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  left: 16px;
  z-index: 1045;
  display: flex;
  justify-content: center;
  pointer-events: none;
  font-family: var(--sd-font-body);
  text-align: left;
}

.ck-inner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  max-width: 880px;
  padding: 18px 20px;
  border: 1px solid var(--sd-border);
  border-radius: 18px;
  background: var(--sd-surface);
  box-shadow: var(--sd-shadow-lg);
  pointer-events: auto;
}

.ck-icon {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--sd-gold-soft);
  font-size: 24px;
  color: var(--sd-gold);
}

.ck-body {
  flex: 1;
  min-width: 0;
}

.ck-title {
  margin: 0 0 4px;
  font-size: 1rem;
  font-weight: 700;
  color: var(--sd-text);
}

.ck-text,
.ck-detail p {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.5;
  color: var(--sd-muted);
}

.ck-detail {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--sd-surface-2);
}

.ck-detail strong {
  color: var(--sd-text);
}

.ck-detail code {
  padding: 0 4px;
  border-radius: 4px;
  background: var(--sd-surface);
  font-size: 0.82rem;
  color: var(--sd-text);
}

.ck-link {
  margin-top: 6px;
  padding: 0;
  border: 0;
  background: none;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--sd-primary);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.ck-actions {
  display: flex;
  flex-shrink: 0;
  align-self: center;
  gap: 8px;
}

/* Mismo peso visual para aceptar y rechazar (requisito de la AEPD) */
.ck-btn {
  min-width: 112px;
  height: 44px;
  padding: 0 18px;
  border: 1px solid var(--sd-primary);
  border-radius: 999px;
  background: var(--sd-primary);
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--sd-primary-contrast);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.ck-btn:hover {
  background: var(--sd-primary-strong);
}

.ck-btn--ghost {
  background: var(--sd-surface);
  color: var(--sd-primary);
}

.ck-btn--ghost:hover {
  background: var(--sd-primary-soft);
  color: var(--sd-primary);
}

.ck-btn:focus-visible,
.ck-link:focus-visible {
  outline: 3px solid var(--sd-gold);
  outline-offset: 2px;
}

.ck-enter-active,
.ck-leave-active {
  transition: opacity 0.25s, transform 0.25s;
}

.ck-enter,
.ck-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

@media (max-width: 680px) {
  .ck {
    right: 10px;
    left: 10px;
  }

  .ck-inner {
    flex-wrap: wrap;
    padding: 16px;
  }

  .ck-icon {
    display: none;
  }

  .ck-actions {
    width: 100%;
  }

  .ck-btn {
    flex: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ck-enter-active,
  .ck-leave-active {
    transition: none;
  }
}
</style>
